/**
 * MONSTER TOWN — DECOR, TOYS & THE TWO ROOM-DECORATING TOOLS.
 * OWNED BY THE INTERIORS CREW. Split out of `furnitureArt.ts` to keep both
 * files under the 500-line house rule; the same `ArtBuilder` contract and the
 * same part-naming contract apply — see the header of `furnitureArt.ts`.
 */

import * as THREE from 'three'
import type { ArtContext } from '../core/placeable'
import { PALETTE, type PaletteName } from '../core/palette'
import { ball, box, boxOn, cone, cylinder, generatedTexture, glassy, group, inked, planeGeo, toon } from '../render/kit'
import { count, flag, grounded, tint } from './furnitureArt'

/**
 * THE LIGHT A LIT SCREEN THROWS. Two shared materials, built once, reused by
 * every telly in every house — the kit rule (never `new THREE.Material()` in a
 * prop) applies just as hard to the glow as to the box it sits on.
 */
let tvHaloMat: THREE.Material | null = null
function tvHaloMaterial(): THREE.Material {
  if (tvHaloMat === null) {
    tvHaloMat = new THREE.MeshBasicMaterial({
      /*
       * A RIM BLOOM, NOT A WHITE WASH.
       *
       * The first version peaked at 0.85 white in the middle, which is exactly
       * where the picture is: additive blending took `monsterSky`, `flowerYellow`
       * and `monsterBubblegum` to the same blown-out white, so the three
       * channels went back to being indistinguishable — the very thing this
       * rebuild was for. The retuned result is `shots/fix-tv-iso/i1-interaction.png`
       * and `shots/fix-tv-iso/i5-close-watching.png`.
       *
       * The glow now stays out of the way of the screen and peaks just OUTSIDE
       * it (the screen fills about half the quad's radius), which is what a lit
       * set does to a dim room anyway.
       */
      map: generatedTexture('tvHalo2', (c, s) => {
        const g = c.createRadialGradient(s / 2, s / 2, s * 0.04, s / 2, s / 2, s * 0.5)
        g.addColorStop(0, 'rgba(255,255,255,0.06)')
        g.addColorStop(0.44, 'rgba(214,240,255,0.11)')
        g.addColorStop(0.62, 'rgba(191,231,245,0.30)')
        g.addColorStop(0.8, 'rgba(143,203,255,0.13)')
        g.addColorStop(1, 'rgba(143,203,255,0)')
        c.clearRect(0, 0, s, s)
        c.fillStyle = g
        c.fillRect(0, 0, s, s)
      }),
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    })
  }
  return tvHaloMat
}

let tvSpillMat: THREE.Material | null = null
function tvSpillMaterial(): THREE.Material {
  if (tvSpillMat === null) {
    tvSpillMat = new THREE.MeshBasicMaterial({
      map: generatedTexture('tvSpill', (c, s) => {
        const g = c.createRadialGradient(s / 2, s * 0.22, s * 0.02, s / 2, s * 0.22, s * 0.62)
        g.addColorStop(0, 'rgba(214,240,255,0.62)')
        g.addColorStop(0.4, 'rgba(160,214,255,0.30)')
        g.addColorStop(0.75, 'rgba(143,203,255,0.09)')
        g.addColorStop(1, 'rgba(143,203,255,0)')
        c.clearRect(0, 0, s, s)
        c.fillStyle = g
        c.fillRect(0, 0, s, s)
      }),
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    })
  }
  return tvSpillMat
}

/**
 * THE THREE SILLY CHANNELS. Data, not code: a fourth show is one more entry.
 * Each is a whole different PICTURE on a whole different colour of screen —
 * the point is that a kid can tell channel 2 from channel 3 across the room,
 * not that the shows are detailed.
 */
const TV_CHANNELS: ReadonlyArray<{
  screen: PaletteName
  draw: (t: number, add: (o: THREE.Object3D) => void) => void
}> = [
  {
    // 1 — a big grinning monster face.
    screen: 'monsterSky',
    draw: (t, add) => {
      add(boxOn(t * 0.44, t * 0.34, t * 0.02, { color: 'monsterLime', y: t * 0.08 }))
      add(ball(t * 0.075, { color: 'eyeWhite', x: -t * 0.11, y: t * 0.3, z: t * 0.02, seg: 8 }))
      add(ball(t * 0.075, { color: 'eyeWhite', x: t * 0.11, y: t * 0.3, z: t * 0.02, seg: 8 }))
      add(ball(t * 0.035, { color: 'pupil', x: -t * 0.11, y: t * 0.3, z: t * 0.04, seg: 6 }))
      add(ball(t * 0.035, { color: 'pupil', x: t * 0.11, y: t * 0.3, z: t * 0.04, seg: 6 }))
      add(boxOn(t * 0.2, t * 0.05, t * 0.02, { color: 'ink', y: t * 0.15, z: t * 0.03 }))
    },
  },
  {
    // 2 — bouncing balls on a sunny field.
    screen: 'flowerYellow',
    draw: (t, add) => {
      add(ball(t * 0.09, { color: 'flowerRed', x: -t * 0.24, y: t * 0.34, z: t * 0.02, seg: 8 }))
      add(ball(t * 0.09, { color: 'uiAccent', x: 0, y: t * 0.44, z: t * 0.02, seg: 8 }))
      add(ball(t * 0.09, { color: 'monsterLilac', x: t * 0.24, y: t * 0.3, z: t * 0.02, seg: 8 }))
      add(boxOn(t * 0.86, t * 0.12, t * 0.02, { color: 'leaf', y: t * 0.02 }))
    },
  },
  {
    // 3 — stripes and a star.
    screen: 'monsterBubblegum',
    draw: (t, add) => {
      for (let i = 0; i < 3; i++) {
        add(
          boxOn(t * 0.9, t * 0.08, t * 0.02, {
            color: i % 2 === 0 ? 'flowerWhite' : 'monsterCoral',
            y: t * (0.06 + i * 0.15),
          }),
        )
      }
      add(ball(t * 0.12, { color: 'flowerYellow', x: 0, y: t * 0.44, z: t * 0.03, seg: 8 }))
      add(cone(t * 0.09, t * 0.16, { color: 'uiCoin', x: -t * 0.28, y: t * 0.38, z: t * 0.02, seg: 6 }))
      add(cone(t * 0.09, t * 0.16, { color: 'uiCoin', x: t * 0.28, y: t * 0.38, z: t * 0.02, seg: 6 }))
    },
  },
]

export function artTv(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('tv')
  const on = flag(ctx, 'on', false)
  /*
   * WHICH SHOW IS ON. 0 means off; 1..3 are the channels. Read from the saved
   * instance state so a kid who leaves the telly on channel 3 and comes back
   * tomorrow finds channel 3, not a generic blue rectangle.
   */
  const channel = Math.min(TV_CHANNELS.length, Math.max(0, Math.round(count(ctx, 'channel', 0))))
  /*
   * THE TELLY STANDS ON SOMETHING. The old stand was 0.7 wide under a 1.25-wide
   * screen, so it hid entirely behind the set and the whole thing read as a
   * black slab lying on the floorboards ("the TV sits directly on the boards
   * with no stand"). It is now a proper low cabinet, wider than the screen is
   * deep, with a shelf you can see and a lighter top so the set has something to
   * sit ON rather than in front of.
   */
  const cab = boxOn(t * 1.24, t * 0.34, t * 0.64, { color: 'wood', name: 'stand' })
  g.add(inked(cab))
  g.add(boxOn(t * 1.3, t * 0.06, t * 0.7, { color: 'woodLight', y: t * 0.34 }))
  g.add(boxOn(t * 1.0, t * 0.16, t * 0.05, { color: 'woodDark', y: t * 0.1, z: t * 0.33 }))
  const body = boxOn(t * 1.25, t * 0.8, t * 0.3, { color: tint(ctx, 'ink'), y: t * 0.4, name: 'body' })
  g.add(inked(body))
  /*
   * `screen` / `screenOff` are GROUPS: turning the telly on swaps the lit panel
   * AND the silly picture on it in one toggle.
   *
   * ROUND-3 REBUILD — "tapping the TV produces almost no visible response".
   * The old lit state was a pale `skyHorizon` rectangle with three dots on it,
   * about forty pixels across at interior zoom, and all three channels drew the
   * SAME three dots: three taps in a row changed nothing a kid could see, and
   * the first tap barely changed anything either. Now the on state carries
   * everything below, so that a glance from across the room says "the telly is
   * on" without the kid having to find the screen and compare it to memory:
   *
   *   - a bright, SATURATED screen, a different colour per channel;
   *   - a different picture per channel (`chan1`..`chan3`);
   *   - a soft halo standing in front of the glass;
   *   - light spilling onto the floorboards in front of the set.
   *
   * All of it lives inside the `screen` group, so the existing on/off toggle
   * drives the lot and nothing new is needed in the animation runtime.
   */
  const screen = group('screen')
  for (let i = 0; i < TV_CHANNELS.length; i++) {
    const spec = TV_CHANNELS[i]
    if (spec === undefined) continue
    const chan = group(`chan${i + 1}`)
    const panel = boxOn(t * 1.02, t * 0.6, t * 0.06, { color: spec.screen, y: t * 0.46, z: t * 0.15 })
    // Flat, not toon: a lit screen is its own light source and must not be
    // shaded down on the side the sun is not on.
    panel.material = toon(spec.screen, { flat: true })
    chan.add(panel)
    const picture = group(`pic${i + 1}`)
    picture.position.set(0, t * 0.46, t * 0.19)
    spec.draw(t, (o) => picture.add(o))
    chan.add(picture)
    // Only the live channel is visible; the toggles below swap them.
    chan.visible = channel === i + 1
    screen.add(chan)
  }

  // The glow is skipped for build ghosts — a preview is already translucent and
  // two additive quads on top of it read as a bug, not as a lit telly.
  if (!ctx.ghost) {
    // The halo in front of the glass. Upright — `planeGeo` is built flat.
    const halo = new THREE.Mesh(planeGeo(t * 2.1, t * 1.5), tvHaloMaterial())
    halo.rotation.x = Math.PI / 2
    halo.position.set(0, t * 0.76, t * 0.24)
    halo.renderOrder = 2
    halo.name = 'glow'
    screen.add(halo)

    // The light on the floorboards. Sits just in front of the cabinet.
    const spill = new THREE.Mesh(planeGeo(t * 2.4, t * 2.2), tvSpillMaterial())
    spill.position.set(0, 0.022 * t, t * 0.95)
    spill.renderOrder = 1
    spill.name = 'spill'
    screen.add(spill)
  }

  screen.visible = on
  const screenOff = group('screenOff')
  screenOff.add(boxOn(t * 1.02, t * 0.6, t * 0.06, { color: 'inkSoft', y: t * 0.46, z: t * 0.15 }))
  screenOff.visible = !on
  // The set moved up onto its new cabinet; the two screen groups ride with it.
  const face = group('face')
  face.position.y = t * 0.04
  face.add(screen, screenOff)
  g.add(face)
  /*
   * THE AERIAL SITS ON TOP OF THE SET, NOT THROUGH IT.
   *
   * Both rods used to start at y = 1.2 with the set's own body spanning 0.4 to
   * 1.2 and z = -0.15..0.15 — so a rod at z = -0.05 began INSIDE the cabinet and
   * came out through the bezel. The critic caught it at close range: "the TV's
   * two antenna rods start inside the screen and pass through the bezel". They
   * now stand on a little foot on the set's top face, behind the screen, and
   * splay outwards like an aerial actually does.
   */
  const aerial = group('aerial')
  aerial.position.set(0, t * 1.2, -t * 0.11)
  aerial.add(cylinder(t * 0.07, t * 0.05, { color: 'metalDark' }))
  for (const s of [-1, 1]) {
    const rod = cylinder(t * 0.028, t * 0.34, { color: 'metalDark', y: t * 0.05 })
    rod.position.x = s * t * 0.03
    rod.rotation.z = -s * 0.42
    aerial.add(rod)
    aerial.add(ball(t * 0.045, { color: 'metal', x: s * t * 0.1, y: t * 0.375, seg: 6 }))
  }
  g.add(aerial)
  grounded(g, ctx, t * 1.5, t * 0.85)
  return g
}

export function artPoster(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('poster')
  const sheet = group('sheet')
  sheet.position.set(0, t * 0.9, -t * 0.36)
  const paper = boxOn(t * 0.9, t * 0.9, t * 0.06, { color: 'uiPanel', name: 'paper' })
  sheet.add(inked(paper))
  // a friendly monster face, drawn in blocks
  const c = tint(ctx, 'monsterBubblegum')
  sheet.add(boxOn(t * 0.56, t * 0.52, t * 0.04, { color: c, y: t * 0.18, z: t * 0.04 }))
  sheet.add(ball(t * 0.07, { color: 'eyeWhite', x: -t * 0.12, y: t * 0.52, z: t * 0.08, seg: 8 }))
  sheet.add(ball(t * 0.07, { color: 'eyeWhite', x: t * 0.12, y: t * 0.52, z: t * 0.08, seg: 8 }))
  sheet.add(ball(t * 0.035, { color: 'pupil', x: -t * 0.12, y: t * 0.52, z: t * 0.11, seg: 6 }))
  sheet.add(ball(t * 0.035, { color: 'pupil', x: t * 0.12, y: t * 0.52, z: t * 0.11, seg: 6 }))
  sheet.add(boxOn(t * 0.24, t * 0.06, t * 0.03, { color: 'ink', y: t * 0.3, z: t * 0.08 }))
  g.add(sheet)
  return g
}

export function artClock(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('clock')
  const case_ = boxOn(t * 0.62, t * 0.62, t * 0.2, { color: tint(ctx, 'wood'), y: t * 1.0, name: 'case' })
  g.add(inked(case_))
  g.add(boxOn(t * 0.46, t * 0.46, t * 0.05, { color: 'uiPanel', y: t * 1.08, z: t * 0.1 }))
  const hands = group('hands')
  hands.position.set(0, t * 1.31, t * 0.14)
  hands.add(box(t * 0.05, t * 0.3, t * 0.03, { color: 'ink', y: t * 0.1 }))
  hands.add(box(t * 0.2, t * 0.05, t * 0.03, { color: 'uiDanger', x: t * 0.07 }))
  g.add(hands)
  // roof + pendulum so it reads as a cuckoo clock, not a dinner plate
  g.add(cone(t * 0.46, t * 0.24, { color: 'roofRed', y: t * 1.62, seg: 4 }))
  g.add(cylinder(t * 0.02, t * 0.4, { color: 'metalDark', y: t * 0.6 }))
  g.add(cylinder(t * 0.1, t * 0.04, { color: 'uiCoin', y: t * 0.56, name: 'weight' }))
  grounded(g, ctx, t * 0.7, t * 0.5)
  return g
}

export function artToybox(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('toybox')
  const body = boxOn(t * 1.0, t * 0.56, t * 0.72, { color: tint(ctx, 'wallBlue'), name: 'body' })
  g.add(inked(body))
  const toys = group('toys')
  toys.visible = false
  toys.add(ball(t * 0.14, { color: 'flowerRed', x: -t * 0.2, y: t * 0.62, seg: 8 }))
  toys.add(boxOn(t * 0.2, t * 0.2, t * 0.2, { color: 'monsterLime', x: t * 0.22, y: t * 0.5 }))
  toys.add(cone(t * 0.12, t * 0.22, { color: 'uiCoin', y: t * 0.54, z: t * 0.2, seg: 8 }))
  g.add(toys)
  const lid = group('lid')
  lid.position.set(0, t * 0.56, -t * 0.36)
  lid.add(boxOn(t * 1.04, t * 0.12, t * 0.76, { color: 'woodLight', z: t * 0.36 }))
  lid.add(boxOn(t * 0.22, t * 0.06, t * 0.1, { color: 'metalDark', y: t * 0.12, z: t * 0.7 }))
  g.add(lid)
  grounded(g, ctx, t * 1.0, t * 0.9)
  return g
}

export function artFishTank(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('fishTank')
  g.add(boxOn(t * 1.0, t * 0.42, t * 0.6, { color: 'woodDark', name: 'stand' }))
  /*
   * THE GLASS HAS TO BE GLASS. It was a solid `toon('water')` box, which is an
   * opaque blue brick — the fish, the sand and the whole point of an aquarium
   * were sealed inside it ("the aquarium contains no fish"). `glassy` is the
   * kit's translucent material and costs the same one draw call.
   */
  const glass = boxOn(t * 0.96, t * 0.7, t * 0.56, { color: 'water', y: t * 0.42, name: 'glass' })
  glass.material = glassy('water', 0.42)
  glass.castShadow = false
  glass.renderOrder = 3
  g.add(boxOn(t * 0.9, t * 0.08, t * 0.5, { color: 'sand', y: t * 0.44 }))
  const fish = group('fish')
  fish.position.set(0, t * 0.78, 0)
  for (const [dx, dy, col] of [
    [-0.2, 0.0, 'uiPrimary'],
    [0.22, 0.14, 'monsterSunny'],
  ] as const) {
    const f = group('oneFish')
    f.position.set(dx * t, dy * t, 0)
    f.add(ball(t * 0.09, { color: col as PaletteName, seg: 8 }))
    f.add(cone(t * 0.07, t * 0.12, { color: col as PaletteName, x: -t * 0.11, seg: 6 }))
    fish.add(f)
  }
  g.add(fish)
  // A shallow water line INSIDE the glass, so the tank reads as full without
  // hiding what is swimming in it.
  const level = boxOn(t * 0.9, t * 0.5, t * 0.5, { color: 'water', y: t * 0.5 })
  level.material = glassy('water', 0.3)
  level.castShadow = false
  level.renderOrder = 2
  g.add(level)
  // Glass last: it is transparent, so it has to be drawn over its own contents.
  g.add(glass)
  g.add(boxOn(t * 0.9, t * 0.06, t * 0.5, { color: 'metal', y: t * 1.12 }))
  grounded(g, ctx, t * 1.0, t * 0.8)
  return g
}

export function artCakeStand(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('cakeStand')
  g.add(cylinder(t * 0.3, t * 0.06, { color: 'metal', name: 'foot' }))
  g.add(cylinder(t * 0.06, t * 0.34, { color: 'metal', y: t * 0.06 }))
  const plate = cylinder(t * 0.46, t * 0.06, { color: 'plate', y: t * 0.4, seg: 16, name: 'plate' })
  g.add(inked(plate))
  const cake = group('cake')
  cake.position.y = t * 0.46
  cake.add(cylinder(t * 0.34, t * 0.22, { color: tint(ctx, 'iceStrawberry'), seg: 14 }))
  cake.add(cylinder(t * 0.35, t * 0.06, { color: 'wallCream', y: t * 0.22, seg: 14 }))
  cake.add(ball(t * 0.07, { color: 'cherry', y: t * 0.32, seg: 8, name: 'cherry' }))
  g.add(cake)
  grounded(g, ctx, t * 0.75, t * 0.75)
  return g
}

// ---------------------------------------------------------------------------
// the two room-decorating toys (LAW #1 doing real work: they repaint the room)
// ---------------------------------------------------------------------------

export function artPaintCan(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('paintCan')
  const can = cylinder(t * 0.26, t * 0.4, { color: 'metal', name: 'can' })
  g.add(inked(can))
  g.add(cylinder(t * 0.24, t * 0.06, { color: tint(ctx, 'wallpaperMint'), y: t * 0.4, name: 'lid' }))
  g.add(box(t * 0.04, t * 0.04, t * 0.5, { color: 'metalDark', y: t * 0.42, rotY: 0 }))
  const brush = group('brush')
  brush.position.set(t * 0.28, t * 0.1, 0)
  brush.add(cylinder(t * 0.04, t * 0.5, { color: 'woodLight' }))
  brush.add(boxOn(t * 0.16, t * 0.16, t * 0.1, { color: tint(ctx, 'wallpaperMint'), y: t * 0.46 }))
  brush.rotation.z = 0.35
  g.add(brush)
  grounded(g, ctx, t * 0.7, t * 0.7)
  return g
}

/**
 * THE WAY OUT: A DOORMAT WITH A PICTURE OF WHERE IT GOES.
 *
 * Flat on purpose — max height 0.09 of a tile. The first version of a room exit
 * was a door leaf standing on the floor. Under a cut-away dollhouse camera that
 * is a slab of wood standing in open space with no wall attached, and it
 * happened in three scenes at once. A mat cannot do that: it is never culled, it
 * never occludes what is behind it, it reads the same from all four rotations,
 * and it is a fat 1-tile tap target for a 3-year-old.
 *
 * ROUND-2 BLOCKER, FIXED HERE, TWICE OVER.
 *
 * 1. It used to be a `uiOk` chevron on a `uiPanel` card on a `uiPrimary` mat
 *    with a `uiCoin` glow ring. Under a fixed isometric camera that silhouette
 *    is a green tick on a card — five confirm buttons scattered across the
 *    living-room floor, brighter than the couch. It was also literally built out
 *    of the UI palette, which is palette drift wearing geometry as a disguise.
 *    It is now a woven doormat in FURNISHING colours: wool, wood, jute. Nothing
 *    on it comes from the `ui*` range.
 *
 * 2. Four doors in one room all looked the same, so heading outside was a one-
 *    in-four guess. Each mat now carries a PICTURE of the place it leads to —
 *    a bed for the bedroom, a bath for the bathroom, a pot for the kitchen, a
 *    sunny doorstep for outside (LAW #4, no words). The picture is drawn in
 *    code, cached per destination, and comes from the instance's saved `to`
 *    state, so a mat placed by `ensureExitMats` knows where it goes and a mat a
 *    kid drags around keeps knowing.
 *
 * The whole mat is also pushed toward the doorway it serves (`rot` aims it, the
 * placer sets `rot`), so it tucks against the threshold board instead of sitting
 * out in the middle of the floor a kid wants to decorate.
 */

/** Destination pictures. Add a case, get a new door — no engine change. */
export type ExitPicture = 'outside' | 'living' | 'kitchen' | 'bed' | 'bath'

const EXIT_PICTURES: readonly ExitPicture[] = ['outside', 'living', 'kitchen', 'bed', 'bath']

function exitPictureOf(ctx: ArtContext): ExitPicture {
  const v = ctx.instance?.state?.['to']
  return EXIT_PICTURES.includes(v as ExitPicture) ? (v as ExitPicture) : 'outside'
}

/**
 * Chunky, high-contrast, drawn at 128 and only ever seen a tile wide.
 *
 * ROUND-3: DRAWN INTO A CIRCLE, WITH THE CORNERS LEFT TRANSPARENT.
 *
 * As a filled square inside a wooden rectangle it was, unavoidably, a framed
 * picture — and four of them lying on the floor of the first room a kid ever
 * walks into read as dropped junk, not as the way out. A round badge cannot read
 * as a framed picture: circles are signs. Same drawing, clipped to a disc, with
 * a rim instead of a frame.
 */
function drawExitPicture(c: CanvasRenderingContext2D, s: number, pic: ExitPicture): void {
  const P = PALETTE
  c.clearRect(0, 0, s, s)
  c.save()
  c.beginPath()
  c.arc(s / 2, s / 2, s * 0.46, 0, Math.PI * 2)
  c.clip()
  c.fillStyle = P.wallpaperCream
  c.fillRect(0, 0, s, s)
  /*
   * The four pictures below are composed for a SQUARE. Clipping a square
   * composition to a disc eats the corners — the sun and the top of the tree in
   * `outside`, the tap in `bath`. Shrinking the drawing about its centre first
   * puts the whole picture inside the circle, so nothing important is cropped.
   */
  c.translate(s / 2, s / 2)
  c.scale(0.84, 0.84)
  c.translate(-s / 2, -s / 2)
  const u = s / 32
  const rect = (x: number, y: number, w: number, h: number, col: string): void => {
    c.fillStyle = col
    c.fillRect(x * u, y * u, w * u, h * u)
  }
  const disc = (x: number, y: number, r: number, col: string): void => {
    c.fillStyle = col
    c.beginPath()
    c.arc(x * u, y * u, r * u, 0, Math.PI * 2)
    c.fill()
  }
  switch (pic) {
    case 'outside': {
      // sky, sun, grass, a little tree: the doorstep, not a room
      rect(0, 0, 32, 20, P.skyHorizon)
      disc(24, 7, 4.5, P.uiCoin)
      rect(0, 19, 32, 13, P.grass)
      rect(9, 20, 3, 8, P.trunk)
      disc(10.5, 16, 6.5, P.leaf)
      disc(10.5, 12, 4.5, P.leafLight)
      break
    }
    case 'living': {
      rect(0, 0, 32, 32, P.wallpaperCream)
      rect(4, 14, 24, 10, P.fabric)
      rect(4, 9, 24, 7, P.fabric)
      rect(7, 13, 8, 4, P.cushion)
      rect(17, 13, 8, 4, P.cushion)
      rect(5, 24, 3, 4, P.woodDark)
      rect(24, 24, 3, 4, P.woodDark)
      break
    }
    case 'kitchen': {
      rect(0, 0, 32, 32, P.wallpaperMint)
      // a cooking pot with steam
      rect(6, 15, 20, 12, P.metalDark)
      rect(4, 13, 24, 3, P.metal)
      rect(13, 9, 6, 3, P.metal)
      disc(11, 6, 2.6, P.wallCream)
      disc(17, 4, 3.2, P.wallCream)
      disc(22, 7, 2.2, P.wallCream)
      break
    }
    case 'bed': {
      rect(0, 0, 32, 32, P.wallLilac)
      rect(3, 10, 26, 5, P.wood)
      rect(3, 15, 26, 8, P.fabricAlt)
      rect(5, 11, 9, 4, P.wallCream)
      rect(3, 23, 3, 5, P.woodDark)
      rect(26, 23, 3, 5, P.woodDark)
      break
    }
    case 'bath': {
      rect(0, 0, 32, 32, P.wallBlue)
      rect(4, 14, 24, 11, P.uiPanel)
      rect(6, 15, 20, 5, P.water)
      disc(10, 11, 3, P.wallCream)
      disc(17, 8, 2.2, P.wallCream)
      rect(4, 25, 3, 4, P.metalDark)
      rect(25, 25, 3, 4, P.metalDark)
      break
    }
  }
  c.restore()
  // A cream rim and an ink edge: a badge on a post, the way every sign a
  // three-year-old has ever seen is built.
  c.strokeStyle = P.wallpaperCream
  c.lineWidth = s * 0.07
  c.beginPath()
  c.arc(s / 2, s / 2, s * 0.435, 0, Math.PI * 2)
  c.stroke()
  c.strokeStyle = P.ink
  c.lineWidth = s * 0.035
  c.beginPath()
  c.arc(s / 2, s / 2, s * 0.465, 0, Math.PI * 2)
  c.stroke()
}

const pictureMats = new Map<ExitPicture, THREE.Material>()
function exitPictureMaterial(pic: ExitPicture): THREE.Material {
  const cached = pictureMats.get(pic)
  if (cached !== undefined) return cached
  const m = new THREE.MeshBasicMaterial({
    map: generatedTexture(`exitBadge:${pic}`, (c, s) => drawExitPicture(c, s, pic)),
    // The badge is a disc drawn into a square canvas; the corners must not paint.
    transparent: true,
    depthWrite: false,
  })
  pictureMats.set(pic, m)
  return m
}

/**
 * THE WAY OUT, ROUND 3.
 *
 * What was here: a wooden rectangle lying on the floor with a rectangular
 * painted scene set into it, pushed a quarter-tile toward its wall so it hung
 * over the edge of the floor slab. The critic's verdict was exact — "framed
 * pictures lying flat on the floor, some half off the floor edge... reads as
 * dropped junk, not the way out" — and it was the first thing a kid saw, in the
 * house, the ice-cream shop and the town shop.
 *
 * Three changes, each aimed at one half of that sentence:
 *
 *  1. NOT A RECTANGLE. Round jute mat, round destination badge, no wooden frame
 *     and no ink rectangle anywhere. A circle on a circle cannot read as a
 *     picture in a frame.
 *  2. IT STANDS UP A LITTLE. Two chunky bollards with warm lamp caps flank the
 *     mat — the same pair that marks the front doorstep out in the town, so the
 *     symbol is already learned. Low (0.44 of a tile) so it can never occlude
 *     the room or read as a door leaf floating in mid-floor, which is what the
 *     round-2 version was killed for.
 *  3. IT STAYS INSIDE ITS OWN SQUARE. Radius 0.42 and a 0.05 nudge toward the
 *     doorway instead of 0.24, so nothing crosses the floor edge at any
 *     rotation. `interiorFitReport` measures exactly this and must come back
 *     negative.
 */
export function artExitMat(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('exitMat')
  // A whisper toward the threshold board — enough to tuck against the doorway,
  // small enough that the art never leaves its tile. `rot` aims local +Z at the
  // wall (see MAT_ROT in house.ts).
  const body = group('body')
  body.position.z = t * 0.04
  g.add(body)

  // 0.40 + the 0.035 the ink shell adds + the 0.04 nudge = 0.475, inside the
  // half-tile. Do not grow any of the three without shrinking another.
  const R = t * 0.4
  // Jute border, wool field: a doormat, in furnishing colours. Never `ui*` —
  // this is furniture, not a button.
  const jute = tint(ctx, 'wood')
  body.add(inked(cylinder(R, t * 0.05, { color: jute, seg: 18, name: 'mat' })))
  body.add(cylinder(R * 0.88, t * 0.025, { color: 'woodLight', y: t * 0.05, seg: 18 }))

  // THE BADGE THAT SAYS WHERE IT GOES. Round, rimmed, lying flat so it reads at
  // every one of the four camera rotations without ever facing away — and big
  // enough to actually read: it fills the mat's whole wool field rather than
  // sitting in the middle of it like a coin.
  const pic = new THREE.Mesh(planeGeo(R * 1.7, R * 1.7), exitPictureMaterial(exitPictureOf(ctx)))
  pic.position.y = t * 0.08
  pic.renderOrder = 2
  pic.name = 'arrow' // the `bounce` clip target — kept for the content def
  body.add(pic)

  /*
   * TWO LITTLE BOLLARDS, ONE EACH SIDE OF THE DOORWAY.
   *
   * This is the part that turns a mat into a door. Two of the four mats in the
   * living room stand against walls the cut-away has removed, so they had
   * nothing at all to belong to and read as objects dropped at random around the
   * edge of the floor. A pair of posts is self-supporting: it says "opening"
   * whether or not there is a wall behind it, and it matches the doorstep the
   * world crew already builds outside the front door.
   */
  for (const s of [-1, 1]) {
    const x = s * R * 0.95
    body.add(cylinder(t * 0.062, t * 0.34, { color: 'woodDark', x, z: -t * 0.02 }))
    const cap = cylinder(t * 0.085, t * 0.1, { color: 'sunWarm', x, y: t * 0.34, z: -t * 0.02 })
    cap.castShadow = false
    body.add(cap)
  }
  return g
}

export function artFloorSample(ctx: ArtContext): THREE.Object3D {
  const t = ctx.grid.tileSize
  const g = group('floorSample')
  const stack = group('swatch')
  const cols: PaletteName[] = ['floorWood', 'floorTile', 'wallCream', 'floorRug']
  for (let i = 0; i < cols.length; i++) {
    const s = boxOn(t * 0.62 - i * t * 0.03, t * 0.06, t * 0.62 - i * t * 0.03, {
      color: cols[i] ?? 'floorWood',
      y: t * (0.02 + i * 0.07),
      rotY: i * 0.18,
    })
    stack.add(s)
  }
  g.add(stack)
  g.add(boxOn(t * 0.1, t * 0.34, t * 0.1, { color: 'woodDark', x: t * 0.3, z: -t * 0.28, name: 'peg' }))
  grounded(g, ctx, t * 0.8, t * 0.8)
  return g
}
